import { useMutation, useQueryClient } from '@tanstack/react-query';
import { auth } from '../config/firebase';
import type { Brand } from './useBrands';
import { industries } from '../components/common/IndustrySelect';

interface UpdateBrandPayload {
  name?: string;
  description?: string;
  website?: string;
  industryId?: string;
  industry?: string;
  values?: string[];
  keywords?: string[];
  languages?: string[];
  contentTone?: string;
  voice?: string;
  uniqueSellingPoints?: string[];
  recommendedFormats?: string[];
  socialMedias?: Array<{
    platform: string;
    frequency: string;
  }>;
  themes?: string[];
  contentGaps?: string[];
  engagementTactics?: string[];
  growthOpportunities?: string[];
  summary?: {
    recap: string;
    takeaways: string[];
  };
  visualIdentity?: {
    colorPalette: {
      primary: string[];
      secondary: string[];
      accent: string[];
      neutral: string[];
      recommended?: {
        complementary: string[];
        improvements: string;
      };
    };
    typography: {
      headings: {
        h1: string;
        h2: string;
        h3: string;
      };
      body: {
        primary: string;
        secondary: string;
      };
      specialty: {
        buttons: string;
        captions: string;
        navigation: string;
      };
      fallbacks: string[];
      recommendations?: {
        improvements: string;
        hierarchy: string;
      };
    };
  };
  visualContentStrategy?: {
    colorUsage: {
      socialMediaPosts: string;
      callToActions: string;
      backgrounds: string;
      seasonalAdaptations: string;
    };
    typographyGuidelines: {
      socialMediaHeaders: string;
      bodyText: string;
      quotesTestimonials: string;
      hashtagsKeywords: string;
    };
    visualElementsToEmphasize: string[];
    designConsistencyTips: string[];
    brandExtensions: {
      socialTemplates: string;
      logoUsage: string;
      imageFilters: string;
    };
  };
  contentStrategy?: {
    themes: string[];
    contentGaps: string[];
    engagementTactics: string[];
    growthOpportunities: string[];
    recommendedFormats?: string[];
    recommendedPlatforms?: Array<{
      platform: string;
      postingFrequency: string;
    }>;
  };
  overview?: {
    summary: string;
    takeaways: string[];
  };
  goToMarketStrategy?: {
    short: string;
    targetSegments: string[];
    keyPersonaTypes: string[];
    marketScope: string;
    priorityMarkets: string[];
    businessModel: string;
    distributionType: string;
  };
}

export const useBrandMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ brandId, data }: { brandId: string; data: UpdateBrandPayload }) => {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      console.log(`Updating brand ${brandId} with data:`, data);

      // Transform data to include industry name if industryId is present
      const apiData = { ...data };
      if (data.industryId) {
        const selectedIndustry = industries.find(ind => ind.id === data.industryId);
        apiData.industry = selectedIndustry?.name || data.industryId;
      }

      try {
        const response = await fetch(`https://api.socialness.ai/management/brands/${brandId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(apiData)
        });

        console.log('PUT response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error response from API:', errorText);
          
          try {
            // Try to parse the error as JSON if possible
            const errorJson = JSON.parse(errorText);
            throw new Error(errorJson.message || `Failed to update brand (${response.status})`);
          } catch (parseError) {
            // If parsing fails, use the raw error text
            throw new Error(`Failed to update brand (${response.status}): ${errorText}`);
          }
        }

        const responseData = await response.json();
        console.log('Brand updated successfully:', responseData);
        return responseData;
      } catch (error) {
        console.error('Error in brand mutation:', error);
        throw error;
      }
    },
    onSuccess: () => {
      // Invalidate and refetch brands query
      queryClient.invalidateQueries({ queryKey: ['brands'] });
    }
  });
};